import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'; // date-fns v2
import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { alpha, useTheme } from '@mui/material/styles';

export type DatePickerProps = {
  label?: string;
  startDate: Date | string | number | null;
  endDate?: Date | string | number | null;
  onChange: (timeRange: {
    startDate: string | null;
    endDate: string | null;
  }) => void;
  disabled?: boolean;
  asSingle?: boolean;
  useRange?: boolean;
};

const parseDateOnlyString = (value: string): Date | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, yearStr, monthStr, dayStr] = match;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const date = new Date(year, month - 1, day);

  if (
    !isValid(date) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

export const DatePicker = ({
  label = '',
  startDate,
  endDate,
  onChange,
  disabled,
  asSingle = false,
  useRange = true,
}: DatePickerProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const [viewMode, setViewMode] = useState<'calendar' | 'month' | 'year'>('calendar');

  const toDate = (val: Date | string | number | null | undefined): Date => {
    if (!val || val === 0) return new Date();
    if (val instanceof Date) return isValid(val) ? val : new Date();
    let date: Date;
    if (typeof val === 'string') {
      date = parseDateOnlyString(val) ?? parseISO(val);
    } else if (typeof val === 'number') {
      const isSeconds = val < 100000000000;
      date = new Date(isSeconds ? val * 1000 : val);
    } else {
      return new Date();
    }

    if (isValid(date)) {
      if (date.getFullYear() === 1970) return new Date();
      return date;
    }
    return new Date();
  };

  const toDateOrNull = (
    val: Date | string | number | null | undefined,
  ): Date | null => {
    if (!val || val === 0) return null;
    if (val instanceof Date) return isValid(val) ? val : null;
    let date: Date;
    if (typeof val === 'string') {
      date = parseDateOnlyString(val) ?? parseISO(val);
    } else if (typeof val === 'number') {
      const isSeconds = val < 100000000000;
      date = new Date(isSeconds ? val * 1000 : val);
    } else {
      return null;
    }

    if (isValid(date)) {
      if (date.getFullYear() === 1970) return null;
      return date;
    }
    return null;
  };

  const [currentMonth, setCurrentMonth] = useState(() => toDate(startDate));
  const [viewYear, setViewYear] = useState(currentMonth.getFullYear());
  const [localPickingStart, setLocalPickingStart] = useState<Date | null>(null);

  const start = useRange ? toDateOrNull(startDate) : null;
  const end = useRange ? toDateOrNull(endDate) : null;
  const singleDate = asSingle ? toDateOrNull(startDate) : null;

  useEffect(() => {
    if (startDate) {
      setCurrentMonth(toDate(startDate));
    }
  }, [startDate]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setLocalPickingStart(null);
    setViewMode('calendar');
  };

  const handleDateClick = (date: Date) => {
    if (asSingle) {
      onChange({
        startDate: format(date, 'yyyy-MM-dd'),
        endDate: format(date, 'yyyy-MM-dd'),
      });
      handleClose();
      return;
    }

    if (useRange) {
      if (!localPickingStart) {
        setLocalPickingStart(date);
        onChange({ startDate: format(date, 'yyyy-MM-dd'), endDate: null });
      } else {
        if (isSameDay(date, localPickingStart)) {
          setLocalPickingStart(null);
          onChange({ startDate: null, endDate: null });
        } else if (date < localPickingStart) {
          onChange({
            startDate: format(date, 'yyyy-MM-dd'),
            endDate: format(localPickingStart, 'yyyy-MM-dd'),
          });
        } else {
          onChange({
            startDate: format(localPickingStart, 'yyyy-MM-dd'),
            endDate: format(date, 'yyyy-MM-dd'),
          });
        }
        setLocalPickingStart(null);
      }
    }
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDateCal = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDateCal = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const rows = [];
    let daysList = [];
    let day = startDateCal;

    while (day <= endDateCal) {
      for (let i = 0; i < 7; i++) {
        const d = day;
        const isCurrentMonth = isSameMonth(d, monthStart);

        let isSelected = false;
        let isInRange = false;

        if (asSingle && singleDate) {
          isSelected = isSameDay(d, singleDate);
        } else if (useRange) {
          isSelected =
            (start && isSameDay(d, start)) ||
            (end && isSameDay(d, end)) ||
            false;
          if (start && end) {
            isInRange = d > start && d < end;
          }
        }

        const isToday = isSameDay(d, new Date());

        daysList.push(
          <ButtonBase
            key={d.toISOString()}
            onClick={() => handleDateClick(d)}
            sx={{
              position: 'relative',
              width: 32,
              height: 32,
              borderRadius: isSelected ? '8px' : isInRange ? '0px' : '8px',
              fontSize: '0.75rem',
              fontWeight: isSelected || isToday ? 700 : 500,
              color: isSelected
                ? '#ffffff'
                : !isCurrentMonth
                  ? 'text.disabled'
                  : isInRange
                    ? 'primary.main'
                    : 'text.primary',
              bgcolor: isSelected
                ? 'primary.main'
                : isInRange
                  ? alpha(theme.palette.primary.main, 0.1)
                  : isToday
                    ? alpha(theme.palette.primary.main, 0.05)
                    : 'transparent',
              boxShadow: isSelected ? '0 2px 6px rgba(16, 185, 129, 0.3)' : 'none',
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: isSelected
                  ? 'primary.dark'
                  : alpha(theme.palette.primary.main, 0.15),
              },
            }}
          >
            {format(d, 'd')}
          </ButtonBase>,
        );
        day = addDays(day, 1);
      }
      rows.push(
        <Box key={day.toISOString()} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {daysList}
        </Box>,
      );
      daysList = [];
    }
    return rows;
  };

  const renderMonthPicker = () => {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, py: 1 }}>
        {months.map((month, idx) => {
          const isSelected = currentMonth.getMonth() === idx;
          return (
            <ButtonBase
              key={month}
              onClick={() => {
                const newDate = new Date(currentMonth);
                newDate.setMonth(idx);
                setCurrentMonth(newDate);
                setViewMode('calendar');
              }}
              sx={{
                py: 1,
                borderRadius: '10px',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: isSelected ? '#ffffff' : 'text.primary',
                bgcolor: isSelected ? 'primary.main' : 'action.hover',
                '&:hover': {
                  bgcolor: isSelected ? 'primary.dark' : 'action.selected',
                },
              }}
            >
              {month}
            </ButtonBase>
          );
        })}
      </Box>
    );
  };

  const renderYearPicker = () => {
    const years = [];
    for (let i = viewYear - 4; i <= viewYear + 7; i++) {
      years.push(i);
    }
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, py: 1 }}>
        {years.map((year) => {
          const isSelected = currentMonth.getFullYear() === year;
          return (
            <ButtonBase
              key={year}
              onClick={() => {
                const newDate = new Date(currentMonth);
                newDate.setFullYear(year);
                setCurrentMonth(newDate);
                setViewYear(year);
                setViewMode('month');
              }}
              sx={{
                py: 1,
                borderRadius: '10px',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: isSelected ? '#ffffff' : 'text.primary',
                bgcolor: isSelected ? 'primary.main' : 'action.hover',
                '&:hover': {
                  bgcolor: isSelected ? 'primary.dark' : 'action.selected',
                },
              }}
            >
              {year}
            </ButtonBase>
          );
        })}
      </Box>
    );
  };

  const displayValue = () => {
    if (asSingle && singleDate && isValid(singleDate)) {
      return format(singleDate, 'MMM d, yyyy');
    }
    if (useRange) {
      if (start && isValid(start) && end && isValid(end)) {
        return `${format(start, 'MM/dd/yy')} - ${format(end, 'MM/dd/yy')}`;
      } else if (start && isValid(start)) {
        return `${format(start, 'MM/dd/yy')} - ...`;
      }
      return 'Please select range';
    }
    return 'Select Date';
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {label && (
        <Typography
          sx={{
            display: 'block',
            mb: 0.75,
            fontSize: '0.6875rem',
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            ml: 0.25,
          }}
        >
          {label}
        </Typography>
      )}

      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          width: '100%',
          height: 40,
          px: 1.5,
          bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
          border: '1px solid',
          borderColor: isOpen ? 'primary.main' : 'divider',
          borderRadius: '12px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxShadow: isOpen
            ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`
            : '0 1px 2px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.6 : 1,
          '&:hover': {
            borderColor: disabled ? 'divider' : 'primary.main',
          },
        }}
      >
        <CalendarTodayRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 500,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color:
              startDate || (useRange && !startDate)
                ? 'text.primary'
                : 'text.disabled',
          }}
        >
          {displayValue()}
        </Typography>
      </Box>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 4,
          sx: {
            width: 280,
            p: 2,
            mt: 1,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 16px 36px -6px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (viewMode === 'year') setViewYear((prev) => prev - 12);
              else setCurrentMonth(subMonths(currentMonth, 1));
            }}
            sx={{ width: 28, height: 28, borderRadius: '8px' }}
          >
            <ChevronLeftRoundedIcon fontSize="small" />
          </IconButton>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <ButtonBase
              onClick={() => setViewMode(viewMode === 'month' ? 'calendar' : 'month')}
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: '6px',
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {format(currentMonth, 'MMMM')}
            </ButtonBase>
            <ButtonBase
              onClick={() => setViewMode(viewMode === 'year' ? 'calendar' : 'year')}
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: '6px',
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {format(currentMonth, 'yyyy')}
            </ButtonBase>
          </Box>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (viewMode === 'year') setViewYear((prev) => prev + 12);
              else setCurrentMonth(addMonths(currentMonth, 1));
            }}
            sx={{ width: 28, height: 28, borderRadius: '8px' }}
          >
            <ChevronRightRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        {useRange && (
          <Box
            sx={{
              mb: 1.5,
              py: 0.5,
              px: 1,
              bgcolor: 'action.hover',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.625rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
              }}
            >
              {!localPickingStart ? 'Select Start Date' : 'Select End Date'}
            </Typography>
          </Box>
        )}

        {viewMode === 'calendar' && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.5,
                mb: 1,
                textAlign: 'center',
              }}
            >
              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
                <Typography
                  key={d}
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                  }}
                >
                  {d}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {renderCalendar()}
            </Box>
          </>
        )}

        {viewMode === 'month' && renderMonthPicker()}
        {viewMode === 'year' && renderYearPicker()}

        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={handleClose}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              textTransform: 'none',
              color: 'primary.main',
            }}
          >
            Close
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};
